import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

class ProductInventory extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    const { signal } = this.#abortController;
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    closestSection?.addEventListener('kraven:variant:instant', this.#handleInstantVariant, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {CustomEvent<{productId?: string, variant?: Record<string, any>}>} event */
  #handleInstantVariant = (event) => {
    const variant = event.detail?.variant;
    if (!variant || String(event.detail?.productId || '') !== String(this.dataset.productId || '')) return;

    const quantity = Number(variant.inventoryQuantity || 0);
    const threshold = Number(this.dataset.inventoryThreshold || 10);
    let status = '';

    if (variant.inventoryManagement === 'shopify') {
      if (quantity > 0) status = quantity <= threshold ? (quantity <= 4 ? 'fast' : 'low') : 'in_stock';
      else if (variant.inventoryPolicy === 'deny') status = 'out_of_stock';
    }

    if (!status) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    for (const className of Array.from(this.classList)) {
      if (className.startsWith('product-inventory--')) this.classList.remove(className);
    }
    this.classList.add(`product-inventory--${status}`);

    const icon = this.querySelector('.product-inventory__icon');
    if (icon) {
      for (const className of Array.from(icon.classList)) {
        if (className.startsWith('product-inventory__icon-')) icon.classList.remove(className);
      }
      icon.classList.add(`product-inventory__icon-${status}`);
    }

    const text = this.querySelector('.product-inventory__text');
    if (!text) return;

    if (status === 'fast' || status === 'low') {
      const strong = document.createElement('strong');
      strong.textContent = status === 'fast' ? 'Selling fast' : 'Ready to ship';
      const divider = document.createElement('span');
      divider.className = 'product-inventory__divider';
      divider.setAttribute('aria-hidden', 'true');
      const detail = document.createElement('span');
      detail.textContent =
        this.dataset.showInventoryQuantity === 'true'
          ? status === 'fast'
            ? `Only ${quantity} left`
            : `${quantity} in stock`
          : 'Available now';
      text.replaceChildren(strong, divider, detail);
    } else {
      text.textContent =
        status === 'in_stock' ? this.dataset.inStockLabel || 'In stock' : this.dataset.outOfStockLabel || 'Out of stock';
    }
  };

  /**
   * Handles product select event by updating the inventory.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newInventory = html.querySelector('product-inventory');
        if (!newInventory) return;

        // The inventory copy and its visual urgency tier must change together.
        // A children-only morph does not update the custom element's classes,
        // which could otherwise leave a low-stock variant styled as selling fast.
        for (const className of Array.from(this.classList)) {
          if (className.startsWith('product-inventory--')) this.classList.remove(className);
        }
        for (const className of Array.from(newInventory.classList)) {
          if (className.startsWith('product-inventory--')) this.classList.add(className);
        }

        morph(this, newInventory, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-inventory] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
